import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Check, Leaf, Wind, BookOpen, ArrowLeft } from "lucide-react";
import { withMemberGate } from "@/lib/with-member-gate";
import { Button } from "@/components/ui/button";
import { GuidedBreathingTimer } from "@/components/GuidedBreathingTimer";
import lotus from "@/assets/recovery-board/back-reflection.jpg";

export const Route = createFileRoute("/daily-practice")({
  head: () => ({ meta: [
    { title: "Daily Practice — Recovery Trauma™" },
    { name: "description", content: "A gentle daily pause with grounding, a short guided breathing timer and optional private reflection." },
    { property: "og:title", content: "Daily Practice — Recovery Trauma™" },
    { property: "og:description", content: "Make space for grounding, natural breathing and a kind reflection at your own pace." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: withMemberGate(DailyPractice, "Daily Practice"),
});

function DailyPractice() {
  const [grounded, setGrounded] = useState(false);
  return (
    <main className="mx-auto max-w-2xl px-4 pb-32 pt-6 text-foreground">
      <Button asChild variant="link" className="mb-4 px-0"><Link to="/recovery-board"><ArrowLeft />Recovery Board</Link></Button>
      <header className="flex items-center gap-5 border-b border-border pb-6">
        <img src={lotus} alt="Lotus resting on a peaceful lake" className="h-28 w-20 shrink-0 rounded-lg object-cover" />
        <div className="min-w-0"><h1 className="font-display text-3xl">Daily Practice</h1>
          <p className="mt-2 text-sm leading-relaxed">A small moment for yourself today. Take what feels comfortable; leave the rest.</p></div>
      </header>
      <section className="py-6" aria-labelledby="daily-arrive">
        <h2 id="daily-arrive" className="flex items-center gap-2 text-lg font-semibold"><Leaf className="h-5 w-5" />Arrive</h2>
        <p className="mt-2 text-sm leading-relaxed">Look around and notice three colours. Feel a surface supporting you. Let your eyes stay open and your breath stay natural.</p>
        <Button variant="outline" aria-pressed={grounded} className="rt-choice mt-3" onClick={() => setGrounded(!grounded)}>
          {grounded ? <Check /> : <Leaf />}{grounded ? "A moment noticed" : "I've taken a moment"}
        </Button>
      </section>
      <GuidedBreathingTimer title="Breathe at your own pace" />
      <section className="py-6" aria-labelledby="daily-reflect">
        <h2 id="daily-reflect" className="flex items-center gap-2 text-lg font-semibold"><BookOpen className="h-5 w-5" />A kind reflection</h2>
        <p className="mt-2 text-sm leading-relaxed">What would make today a little gentler? You might think of one small boundary, a rest, or someone you feel safe reaching out to.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button asChild variant="outline"><Link to="/journal"><BookOpen />Open my Journal</Link></Button>
          <Button asChild variant="outline"><Link to="/breathing"><Wind />More breathing practices</Link></Button>
        </div>
      </section>
      <p className="border-t border-border pt-4 text-xs leading-relaxed">This optional wellbeing practice is not therapy or crisis support. If breathing feels uncomfortable, stop and notice the room instead. You do not need to complete anything.</p>
    </main>
  );
}